"use client";

import { c as _c } from "react/compiler-runtime";
import { Collector, Container, makeBusinessIdSetter, makeEventWithPageViewId, type OmitPageViewId, trackerPlugin } from "@grupa-pracuj/analytics-plugin-gp-tracker";
import { schema as pageView } from "@grupa-pracuj/tracker-schematics/events/pl.pracuj.tracker/pageview/3-0-0";
import { type Properties as CookieModal, schema as cookieAgreementsModal } from "@grupa-pracuj/tracker-schematics/events/pl.pracuj.www/cookieagreementsmodal/1-0-0";
import { type Properties as WebVitals, schema as webVitals } from "@grupa-pracuj/tracker-schematics/events/pl.pracuj.www/webvitals/1-0-1";
import Analytics, { type AnalyticsInstance } from "analytics";
import { createContext, type ReactNode, useState } from "react";
import type { Metric } from "web-vitals";
import type { User } from "@/types";
import { collectWebVitals } from "./WebVitals";
type GenericEventMethod<T> = (data: OmitPageViewId<T>) => Promise<unknown>;
type BaseEventMethod = () => Promise<unknown>;
type TrackerProperties = {
  analyticsInstance: AnalyticsInstance;
  sendBaseAnalytics: BaseEventMethod;
  sendCookieAgreementsModal: GenericEventMethod<CookieModal>;
};
export const TrackerContext = createContext<TrackerProperties | null>(null);
export const TrackerProvider = t0 => {
  const $ = _c(21);
  const {
    children,
    environment,
    appName,
    appVersion,
    user
  } = t0;
  const isProduction = environment === "production";
  let t1;
  if ($[0] !== appName || $[1] !== appVersion || $[2] !== isProduction) {
    t1 = () => Analytics({
      app: appName,
      debug: !isProduction,
      plugins: [trackerPlugin({
        appName,
        appVersion: `${appVersion}`,
        collector: isProduction ? Collector.prod : Collector.beta,
        container: Container.pracuj,
        events: {
          cookieAgreementsModal,
          pageView,
          webVitals
        }
      })]
    });
    $[0] = appName;
    $[1] = appVersion;
    $[2] = isProduction;
    $[3] = t1;
  } else {
    t1 = $[3];
  }
  const [analyticsInstance] = useState(t1);
  let t2;
  if ($[4] !== analyticsInstance || $[5] !== user?.userId) {
    t2 = async () => {
      const userId = user?.userId ?? "";
      const businessId = String(userId);
      const previousUrl = analyticsInstance.getState("page.last.properties.url") as string | undefined;
      const referrer = previousUrl ?? document.referrer;
      await makeBusinessIdSetter(analyticsInstance)(businessId);
      await analyticsInstance.page({
        lan: "pl",
        url: globalThis.location.href,
        referrer
      });
    };
    $[4] = analyticsInstance;
    $[5] = user?.userId;
    $[6] = t2;
  } else {
    t2 = $[6];
  }
  const sendPageView = t2;
  let t3;
  if ($[7] !== analyticsInstance) {
    const webVitalsInstance = makeEventWithPageViewId(analyticsInstance, "webVitals");
    t3 = async t4 => {
      const {
        name,
        delta,
        id,
        value: metricValue
      } = t4;
      await webVitalsInstance({
        id,
        name,
        value: metricValue,
        delta
      });
    };
    $[7] = analyticsInstance;
    $[8] = t3;
  } else {
    t3 = $[8];
  }
  const sendWebVitals = t3;
  let t4;
  if ($[9] !== sendPageView || $[10] !== sendWebVitals) {
    t4 = async () => {
      await sendPageView();
      collectWebVitals(metric => {
        sendWebVitals(metric).catch(_temp);
      });
    };
    $[9] = sendPageView;
    $[10] = sendWebVitals;
    $[11] = t4;
  } else {
    t4 = $[11];
  }
  const sendBaseAnalytics = t4;
  let t5;
  if ($[12] !== analyticsInstance) {
    const cookieAgreementsModalInstance = makeEventWithPageViewId(analyticsInstance, "cookieAgreementsModal");
    t5 = async data => {
      await cookieAgreementsModalInstance(data);
    };
    $[12] = analyticsInstance;
    $[13] = t5;
  } else {
    t5 = $[13];
  }
  const sendCookieAgreementsModal = t5;
  let t6;
  if ($[14] !== analyticsInstance || $[15] !== sendBaseAnalytics || $[16] !== sendCookieAgreementsModal) {
    t6 = {
      analyticsInstance,
      sendCookieAgreementsModal,
      sendBaseAnalytics
    };
    $[14] = analyticsInstance;
    $[15] = sendBaseAnalytics;
    $[16] = sendCookieAgreementsModal;
    $[17] = t6;
  } else {
    t6 = $[17];
  }
  const value = t6;
  let t7;
  if ($[18] !== children || $[19] !== value) {
    t7 = <TrackerContext.Provider value={value}>{children}</TrackerContext.Provider>;
    $[18] = children;
    $[19] = value;
    $[20] = t7;
  } else {
    t7 = $[20];
  }
  return t7;
};
function _temp() {}